import type { CSSProperties } from "react";
import { styled, keyframes } from "@pigment-css/react";

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "calc(200px + 100%) 0" },
});

const SkeletonBase = styled("div")(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.fieldSalesTwo.colors.neutral[200]} 25%, ${theme.fieldSalesTwo.colors.neutral[300]} 50%, ${theme.fieldSalesTwo.colors.neutral[200]} 75%)`,
  backgroundSize: "200px 100%",
  animation: `${shimmer} 4s infinite linear`,
}));

// Inline variant so a line can sit inside a text element (e.g. a styled <p>) without producing
// invalid block-in-phrasing markup. The element keeps its line box, so the placeholder inherits the
// real text's height.
const SkeletonBaseInline = styled("span")(({ theme }) => ({
  display: "inline-block",
  verticalAlign: "middle",
  background: `linear-gradient(90deg, ${theme.fieldSalesTwo.colors.neutral[200]} 25%, ${theme.fieldSalesTwo.colors.neutral[300]} 50%, ${theme.fieldSalesTwo.colors.neutral[200]} 75%)`,
  backgroundSize: "200px 100%",
  animation: `${shimmer} 4s infinite linear`,
}));

interface BoxProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}

function SkeletonBox({ width, height, radius = 4, className, style }: BoxProps) {
  return <SkeletonBase className={className} style={{ width, height, borderRadius: radius, ...style }} />;
}

interface LineProps {
  width?: number | string;
  height?: number | string;
  /** Render as an inline span so the line can be placed inside a text element without breaking it. */
  inline?: boolean;
  className?: string;
  style?: CSSProperties;
}

function SkeletonLine({ width = "100%", height = "1em", inline = false, className, style }: LineProps) {
  const Base = inline ? SkeletonBaseInline : SkeletonBase;
  return <Base className={className} style={{ width, height, borderRadius: 4, ...style }} />;
}

interface CircleProps {
  size: number | string;
  className?: string;
  style?: CSSProperties;
}

function SkeletonCircle({ size, className, style }: CircleProps) {
  return <SkeletonBase className={className} style={{ width: size, height: size, borderRadius: "50%", ...style }} />;
}

export const Skeleton = {
  Box: SkeletonBox,
  Line: SkeletonLine,
  Circle: SkeletonCircle,
};
